/**
 * Per-location tax-rates editor (DNKADM-0051 U3) — the list of `v2_tax_rate` rows
 * for one owned location, rendered under the location detail (handoff §5.2).
 *
 * Loads `listTaxRates(locationId, { status })` through `useResource`, keyed on the
 * Active/Inactive/All filter (`useActiveFilter` + `<ActiveFilter>`, §4.3). A table of
 * name → rate% → agency → status, each row with Edit + Deactivate/Reactivate. "Add
 * tax rate" and Edit open the {@link TaxRateDialog}. Every write refetches the list +
 * raises a toast (refetch-after-write).
 *
 * **Tax is exclusive + all-or-none** (§5.2): the editor only manages the LIST of
 * rates — there is no inclusive toggle and no per-service rate picker. A taxable
 * service applies every active rate at the location (taxability is a service flag,
 * §5.5).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  deactivateTaxRate,
  listTaxRates,
  reactivateTaxRate,
  type TaxRate,
} from '@/api/taxRates';
import { TaxRateDialog } from './TaxRateDialog';
import { ScreenError } from '@/components/ScreenError';

interface TaxRatesEditorProps {
  /** The owning location id. */
  locationId: string;
}

/** Render a rate percentage at two decimals (`6` → `6.00`). */
function formatPercent(ratePercent: number): string {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(ratePercent);
}

export function TaxRatesEditor({ locationId }: TaxRatesEditorProps) {
  const showToast = useToast();
  const filter = useActiveFilter();

  const rates = useResource(
    useCallback(
      () => listTaxRates(locationId, { status: filter.status }),
      [locationId, filter.status],
    ),
    [locationId, filter.status],
  );

  // Dialog state: `null` = closed; `{ rate: null }` = add; `{ rate }` = edit.
  const [dialog, setDialog] = useState<{ rate: TaxRate | null } | null>(null);

  const refetch = rates.refetch;

  // Names used by OTHER rates (the dialog excludes the edited rate's own name).
  const usedNames = useMemo(
    () => (rates.data ?? []).map((r) => r.name),
    [rates.data],
  );

  const handleDeactivate = useCallback(
    async (rate: TaxRate) => {
      try {
        await deactivateTaxRate(locationId, rate.id);
        refetch();
        showToast('Tax rate deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the tax rate.', 'error');
      }
    },
    [locationId, refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (rate: TaxRate) => {
      try {
        await reactivateTaxRate(locationId, rate.id);
        refetch();
        showToast('Tax rate reactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the tax rate.', 'error');
      }
    },
    [locationId, refetch, showToast],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Tax rates
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Every active rate is applied (exclusive) to a taxable service at this location.
          </Typography>
        </Box>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter tax rates by status"
          />
          <Button
            variant="contained"
            startIcon={<Icon name="add" />}
            onClick={() => setDialog({ rate: null })}
          >
            Add tax rate
          </Button>
        </Stack>
      </Stack>

      {rates.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={160}
          role="status"
          aria-label="Loading tax rates"
          sx={{ borderRadius: 3 }}
        />
      )}

      {rates.status === 'error' && (
        <ScreenError
          error={rates.error}
          what="the tax rates"
          onRetry={rates.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {rates.status === 'ready' && rates.data && rates.data.length === 0 && (
        <Alert severity="info" icon={<Icon name="percent" />}>
          {filter.value === 'active'
            ? 'No tax rates yet. Add one to charge tax on taxable services at this location.'
            : 'No tax rates match this filter.'}
        </Alert>
      )}

      {rates.status === 'ready' && rates.data && rates.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Tax rates" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rates.data.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>{rate.name === '' ? rate.id : rate.name}</TableCell>
                  <TableCell align="right">{formatPercent(rate.ratePercent)}%</TableCell>
                  <TableCell>{rate.taxAgency ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={rate.isActive ? 'Active' : 'Inactive'}
                      color={rate.isActive ? 'success' : 'default'}
                      variant={rate.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconAction
                      size="small"
                      label={`Edit tax rate ${rate.name || rate.id}`}
                      onClick={() => setDialog({ rate })}
                    >
                      <Icon name="edit" />
                    </IconAction>
                    {rate.isActive ? (
                      <IconAction
                        size="small"
                        label={`Deactivate tax rate ${rate.name || rate.id}`}
                        onClick={() => void handleDeactivate(rate)}
                      >
                        <Icon name="toggle_off" />
                      </IconAction>
                    ) : (
                      <IconAction
                        size="small"
                        label={`Reactivate tax rate ${rate.name || rate.id}`}
                        onClick={() => void handleReactivate(rate)}
                      >
                        <Icon name="toggle_on" />
                      </IconAction>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TaxRateDialog
        open={dialog !== null}
        locationId={locationId}
        rate={dialog?.rate ?? null}
        usedNames={
          // Exclude the edited rate's own name from the duplicate-warning set.
          dialog?.rate
            ? usedNames.filter((n) => n !== dialog.rate?.name)
            : usedNames
        }
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
